// pages/AwsEc2Health.jsx — EC2 Health (AWS group)
// Two tabs: Indomas (ap-southeast-3) and PIL (ap-southeast-1).
// Backend: GET /api/ec2-health?account=indomas|pil[&refresh=1]  (ec2Health.js)
// One card per instance, worst first. Each card answers "is this box healthy?"
// with one verdict + the reasons; details (all mounts, volumes, top processes)
// open on click.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api, useAuth } from '../context/AuthContext';
import { OsConsolePanel } from './FileDeployPage';
import { usePageAIContext } from '../context/AIPageContext';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { InstanceHistory, AccountTrends, TrendChart, fmtMbps } from './Ec2HealthHistory';
import Ec2AppsView, { inventoryTabs, InventoryTabBody, localTime, ago } from './Ec2AppsView';
import Ec2LogsTab from './Ec2LogsTab';
import Ec2TopTab from './Ec2TopTab';
import Ec2AlbView from './Ec2AlbView';
import Ec2OpenSearchView from './Ec2OpenSearchView';
import RdpPanel from './RdpPanel';

const TABS = [
  { key: 'indomas', label: 'Indomas', sub: 'Jakarta · ap-southeast-3' },
  { key: 'pil', label: 'PIL', sub: 'Singapore · ap-southeast-1' },
];
const LS_TAB = 'ec2Health.tab';
const AUTO_MS = 5 * 60 * 1000;

const VERDICT = {
  critical: { label: 'Critical', pill: 'bg-red-100 text-red-700 ring-red-200', bar: 'bg-red-500', ring: 'ring-red-200' },
  warning: { label: 'Warning', pill: 'bg-amber-100 text-amber-700 ring-amber-200', bar: 'bg-amber-500', ring: 'ring-amber-200' },
  ok: { label: 'Healthy', pill: 'bg-emerald-100 text-emerald-700 ring-emerald-200', bar: 'bg-emerald-500', ring: 'ring-gray-200' },
  stopped: { label: 'Stopped', pill: 'bg-gray-100 text-gray-600 ring-gray-200', bar: 'bg-gray-400', ring: 'ring-gray-200' },
};
const SRC_LABEL = { ssm: 'SSM', ssh: 'SSH', cwagent: 'CW Agent' };
const LS_GROUP = 'ec2Health.groupBy';
const LS_VIEW = 'ec2Health.view';
const UNTAGGED = '(untagged)';
const RANK = { critical: 0, warning: 1, ok: 2, stopped: 3 };

function countBy(arr, fn) {
  const m = new Map();
  arr.forEach((x) => { const k = fn(x); m.set(k, (m.get(k) || 0) + 1); });
  return [...m.entries()].sort((a, b) => (a[0] === UNTAGGED) - (b[0] === UNTAGGED) || b[1] - a[1] || a[0].localeCompare(b[0]));
}

// Same shape as the backend summary, for a filtered subset.
function summarize(rows) {
  const run = rows.filter((r) => r.state === 'running');
  return {
    total: rows.length, running: run.length, stopped: rows.filter((r) => r.state === 'stopped').length,
    critical: rows.filter((r) => r.verdict === 'critical').length,
    warning: rows.filter((r) => r.verdict === 'warning').length,
    ok: rows.filter((r) => r.verdict === 'ok').length,
    osCovered: run.filter((r) => r.os).length,
    vcpu: run.reduce((s, r) => s + (r.vcpu || 0), 0),
    memGiB: Math.round(run.reduce((s, r) => s + (r.memGiB || 0), 0) * 10) / 10,
    ebsGiB: rows.reduce((s, r) => s + (r.volumes || []).reduce((a, v) => a + (v.sizeGiB || 0), 0), 0),
  };
}

function fmtAgo(iso) {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return s + 's ago';
  if (s < 3600) return Math.round(s / 60) + 'm ago';
  return Math.round(s / 3600) + 'h ago';
}
function fmtUptime(sec) {
  if (sec == null) return null;
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600);
  return d ? `${d}d ${h}h` : `${h}h ${Math.floor((sec % 3600) / 60)}m`;
}
function sinceLaunch(iso) {
  if (!iso) return null;
  return fmtUptime(Math.round((Date.now() - new Date(iso).getTime()) / 1000));
}
function tone(v, warn, crit) {
  if (v == null) return 'bg-gray-300';
  if (v >= crit) return 'bg-red-500';
  if (v >= warn) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function Meter({ label, value, warn, crit, sub, na }) {
  const w = value == null ? 0 : Math.min(100, Math.max(0, value));
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-medium text-gray-500">{label}</span>
        <span className="font-semibold tabular-nums text-gray-800">{value == null ? <span className="font-normal text-gray-400">{na || 'n/a'}</span> : `${value}%`}</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${tone(value, warn, crit)}`} style={{ width: `${w}%` }} />
      </div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}

function Spark({ data, warn, crit }) {
  const pts = (data || []).map((v, i) => [i, v]).filter((p) => p[1] != null);
  if (pts.length < 2) return <div className="h-8 text-[11px] text-gray-300">no CPU history</div>;
  const W = 120, H = 28;
  const max = Math.max(100 * 0.25, ...pts.map((p) => p[1]));
  const x = (i) => (i / 23) * W;
  const y = (v) => H - (v / max) * (H - 2) - 1;
  const d = pts.map((p, k) => `${k ? 'L' : 'M'}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(' ');
  const peak = Math.max(...pts.map((p) => p[1]));
  const color = peak >= crit ? '#ef4444' : peak >= warn ? '#f59e0b' : '#0ea5e9';
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-8 w-full" preserveAspectRatio="none" aria-label="CPU last 24 hours">
      <path d={`${d} L${x(pts[pts.length - 1][0])},${H} L${x(pts[0][0])},${H} Z`} fill={color} opacity="0.12" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Kpi({ label, value, sub, cls, onClick, active }) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded-xl border bg-white p-3 text-left transition ${onClick ? 'hover:border-gray-300' : 'cursor-default'} ${active ? 'border-sky-400 ring-2 ring-sky-100' : 'border-gray-200'}`}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-0.5 text-2xl font-bold tabular-nums ${cls || 'text-gray-800'}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </button>
  );
}

function worstDisk(os) {
  const ds = (os && os.disks ? os.disks : []).filter((d) => !d.ignored);
  if (!ds.length) return null;
  return ds.reduce((a, b) => (b.usedPct > a.usedPct ? b : a));
}

function InstanceCard({ x, th, onOpen }) {
  const v = VERDICT[x.verdict] || VERDICT.ok;
  const running = x.state === 'running';
  const wd = worstDisk(x.os);
  const os = x.os;
  const naReason = x.osReason ? (x.windows ? 'no agent' : 'no access') : 'n/a';
  return (
    <div className={`flex flex-col rounded-xl border bg-white shadow-sm ring-1 transition hover:shadow-md ${v.ring} ${x.verdict === 'critical' ? 'border-red-200' : x.verdict === 'warning' ? 'border-amber-200' : 'border-gray-200'}`}>
      <button type="button" onClick={onOpen} className="p-4 text-left">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-900" title={x.name}>{x.name}</div>
            <div className="mt-0.5 truncate text-[11px] text-gray-500">
              {x.type}{x.vcpu ? ` · ${x.vcpu} vCPU` : ''}{x.memGiB ? ` · ${x.memGiB} GiB` : ''} · {x.privateIp || '—'}
            </div>
          </div>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${v.pill}`}>{v.label}</span>
        </div>

        {running ? (
          <>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <Meter label="CPU" value={x.cpu && x.cpu.now} warn={th.cpuWarn} crit={th.cpuCrit}
                sub={x.cpu && x.cpu.max24 != null ? `max 24h ${x.cpu.max24}%` : null} />
              <Meter label="RAM" value={os && os.memPct} warn={th.memWarn} crit={th.memCrit} na={naReason}
                sub={os && os.memTotalGiB ? `${os.memUsedGiB} / ${os.memTotalGiB} GiB` : null} />
              <Meter label="Disk" value={wd && wd.usedPct} warn={th.diskWarn} crit={th.diskCrit} na={naReason}
                sub={wd ? wd.mount : null} />
            </div>
            <div className="mt-2"><Spark data={x.cpu && x.cpu.spark} warn={th.cpuWarn} crit={th.cpuCrit} /></div>
          </>
        ) : (
          <div className="mt-3 rounded-lg bg-gray-50 px-3 py-4 text-center text-xs text-gray-500">Instance is {x.state}</div>
        )}

        {x.reasons && x.reasons.length > 0 && (
          <ul className="mt-2 space-y-0.5">
            {x.reasons.slice(0, 3).map((r, i) => (
              <li key={i} className={`flex items-start gap-1.5 text-xs ${r.level === 'critical' ? 'text-red-700' : 'text-amber-700'}`}>
                <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${r.level === 'critical' ? 'bg-red-500' : 'bg-amber-500'}`} />
                <span>{r.text}</span>
              </li>
            ))}
            {x.reasons.length > 3 && <li className="text-[11px] text-gray-400">+{x.reasons.length - 3} more…</li>}
          </ul>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
          {x.appRole && <span className="rounded bg-sky-50 px-1.5 py-px font-medium text-sky-700">{x.appRole}</span>}
          <span>{x.az}</span>
          <span title={x.platform}>{x.osName || x.platform}</span>
          {running && x.net && x.net.inMbps != null && <span title="Network in / out, CloudWatch 15-min average">↓{fmtMbps(x.net.inMbps)} ↑{fmtMbps(x.net.outMbps)}</span>}
          {os && os.uptimeSec != null ? <span>up {fmtUptime(os.uptimeSec)}</span> : running && x.launchTime ? <span>running {sinceLaunch(x.launchTime)}</span> : null}
          {x.osSource ? <span className="rounded bg-gray-100 px-1.5 py-px text-gray-500">via {SRC_LABEL[x.osSource] || x.osSource}</span>
            : running ? <span className="rounded bg-gray-100 px-1.5 py-px text-gray-500" title={x.osReason || ''}>CloudWatch only</span> : null}
          <span className="ml-auto text-sky-600">Details</span>
        </div>
      </button>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-0.5 text-xs">
      <span className="text-gray-500">{k}</span>
      <span className="truncate text-right font-medium text-gray-800">{v == null || v === '' ? '—' : v}</span>
    </div>
  );
}

function InstanceOverview({ x, th }) {
  const os = x.os;
  const s = x.status || {};
  const wd = worstDisk(os);
  const running = x.state === 'running';
  const naReason = x.osReason ? (x.windows ? 'no agent' : 'no access') : 'n/a';
  return (
    <div className="space-y-5">
      {running && (
        <div>
          <div className="grid grid-cols-3 gap-4">
            <Meter label="CPU (15 min)" value={x.cpu && x.cpu.now} warn={th.cpuWarn} crit={th.cpuCrit}
              sub={x.cpu && x.cpu.max24 != null ? `max 24h ${x.cpu.max24}%` : null} />
            <Meter label="RAM" value={os && os.memPct} warn={th.memWarn} crit={th.memCrit} na={naReason}
              sub={os && os.memTotalGiB ? `${os.memUsedGiB} / ${os.memTotalGiB} GiB` : null} />
            <Meter label="Fullest disk" value={wd && wd.usedPct} warn={th.diskWarn} crit={th.diskCrit} na={naReason}
              sub={wd ? wd.mount : null} />
          </div>
          <div className="mt-2"><Spark data={x.cpu && x.cpu.spark} warn={th.cpuWarn} crit={th.cpuCrit} /></div>
          <div className="text-[10px] text-gray-400">CPU, last 24 h (hourly)</div>
        </div>
      )}
      {x.reasons && x.reasons.length > 0 && (
        <ul className="space-y-1 rounded-lg border border-gray-200 bg-white p-3">
          {x.reasons.map((r, i) => (
            <li key={i} className={`flex items-start gap-1.5 text-xs ${r.level === 'critical' ? 'text-red-700' : 'text-amber-700'}`}>
              <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${r.level === 'critical' ? 'bg-red-500' : 'bg-amber-500'}`} />
              <span>{r.text}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Instance</div>
          <Row k="Instance ID" v={x.id} />
          <Row k="Public IP" v={x.publicIp} />
          <Row k="Application role" v={x.appRole} />
          <Row k="Environment" v={x.env} />
          <Row k="Business unit" v={x.tags && x.tags['Business Unit']} />
          <Row k="Owner" v={x.tags && x.tags.Owner} />
          <Row k="Launched" v={x.launchTime ? new Date(x.launchTime).toLocaleString('en-GB') : null} />
          <Row k="System check" v={s.system} />
          <Row k="Instance check" v={s.instance} />
          <Row k="EBS check" v={s.ebs} />
          {x.credit != null && <Row k="CPU credits" v={x.credit} />}
        </div>
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Last 24 hours</div>
          <Row k="CPU avg / max" v={x.cpu ? `${x.cpu.avg24 ?? '—'}% / ${x.cpu.max24 ?? '—'}%` : null} />
          <Row k="Network in / out" v={x.net ? `${x.net.inGiB24} / ${x.net.outGiB24} GiB` : null} />
          {os && os.load && <Row k="Load 1/5/15 m" v={`${os.load.join(' / ')}${os.nproc ? ` (${os.nproc} cores)` : ''}`} />}
          {os && os.swapPct != null && <Row k="Swap used" v={`${os.swapPct}%${os.swapTotalGiB ? ` of ${os.swapTotalGiB} GiB` : ''}`} />}
          {!os && x.osReason && <Row k="OS metrics" v={x.osReason} />}
        </div>
      </div>

      {os && os.disks && os.disks.length > 0 && (
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Filesystems</div>
          <div className="space-y-1.5">
            {[...os.disks].sort((a, b) => b.usedPct - a.usedPct).map((d) => (
              <div key={d.mount} className="grid grid-cols-[minmax(0,1fr)_88px_44px] items-center gap-2 text-xs">
                <span className="truncate text-gray-700" title={d.mount}>
                  {d.mount}{d.ignored && <span className="ml-1 text-[10px] text-gray-400" title="Pre-allocated Sybase device — always near full; excluded from the verdict">(DB device)</span>}
                  {d.inodePct != null && d.inodePct >= th.inodeWarn && <span className="ml-1 text-[10px] text-amber-600">inodes {d.inodePct}%</span>}
                </span>
                <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
                  <div className={`h-full rounded-full ${d.ignored ? 'bg-gray-400' : tone(d.usedPct, th.diskWarn, th.diskCrit)}`} style={{ width: `${Math.min(100, d.usedPct)}%` }} />
                </div>
                <span className="text-right tabular-nums text-gray-600">{d.usedPct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {x.volumes && x.volumes.length > 0 && (
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">EBS volumes ({x.volumes.reduce((a, v) => a + (v.sizeGiB || 0), 0)} GiB)</div>
          <div className="flex flex-wrap gap-1.5">
            {x.volumes.map((v) => (
              <span key={v.id} className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-600" title={v.id}>
                {v.device} · {v.sizeGiB} GiB {v.type}{v.iops ? ` · ${v.iops} IOPS` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {os && os.top && os.top.length > 0 && (
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{x.windows ? 'Top processes by total CPU time' : 'Top processes by CPU (now)'}</div>
          <table className="w-full text-xs">
            <tbody>
              {os.top.map((p) => (
                <tr key={p.pid} className="border-t border-gray-100">
                  <td className="py-0.5 text-gray-400 tabular-nums">{p.pid}</td>
                  <td className="py-0.5 text-gray-700">{p.cmd}</td>
                  <td className="py-0.5 text-right tabular-nums text-gray-600">{p.cpuSec != null ? `${p.cpuSec.toLocaleString('en-US')} s CPU` : `${p.cpu}% CPU`}</td>
                  <td className="py-0.5 text-right tabular-nums text-gray-600">{p.memMiB != null ? `${p.memMiB.toLocaleString('en-US')} MiB` : `${p.mem}% MEM`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Network tab ──────────────────────────────────────────────────────────────
const ENA_LABEL = { bw_in: 'Inbound bandwidth', bw_out: 'Outbound bandwidth', pps: 'Packets per second', conntrack: 'Connection tracking', linklocal: 'Link-local (DNS / NTP / metadata)' };
const gib = (b) => (b == null ? '—' : b >= 2 ** 40 ? `${(b / 2 ** 40).toFixed(2)} TiB` : `${(b / 2 ** 30).toFixed(b >= 10 * 2 ** 30 ? 0 : 1)} GiB`);

function NetStat({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-0.5 text-lg font-bold tabular-nums text-gray-800">{value}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}

function NetworkTab({ x }) {
  const n = x.net || {};
  const nics = (x.os && x.os.nics) || [];
  const enis = x.enis || [];
  const eniByMac = Object.fromEntries(enis.filter((e) => e.mac).map((e) => [e.mac, e]));
  const end = n.sparkEnd ? new Date(n.sparkEnd).getTime() : Date.now();
  const at = (i) => end - (23 - i) * 3600e3 - 1800e3;
  const cwSeries = [
    { name: 'In', color: '#10b981', points: (n.inSpark || []).map((v, i) => [at(i), v]) },
    { name: 'Out', color: '#6366f1', points: (n.outSpark || []).map((v, i) => [at(i), v]) },
  ].filter((s) => s.points.some((p) => p[1] != null));
  const rateAge = nics.find((c) => c.rateSec) ? Math.round(nics.find((c) => c.rateSec).rateSec / 60) : null;
  const withEna = nics.filter((c) => c.ena);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <NetStat label="In now" value={fmtMbps(n.inMbps)} sub="CloudWatch, 15-min average" />
        <NetStat label="Out now" value={fmtMbps(n.outMbps)} sub="CloudWatch, 15-min average" />
        <NetStat label="Peak 24 h (in / out)" value={`${fmtMbps(n.inPeakMbps)} / ${fmtMbps(n.outPeakMbps)}`} sub={`transferred ${n.inGiB24 ?? '—'} / ${n.outGiB24 ?? '—'} GiB`} />
        <NetStat label="Instance network" value={x.netPerf || '—'} sub={`${x.enaSupport ? 'ENA enabled' : 'ENA not enabled'}${x.maxEnis ? ` · up to ${x.maxEnis} ENIs` : ''}`} />
      </div>

      {cwSeries.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-1 text-xs font-medium text-gray-600">Throughput, last 24 h (hourly average)</div>
          <TrendChart series={cwSeries} days={1} unit="Mbps" height={130} />
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-2 flex flex-wrap items-baseline gap-2">
          <h3 className="text-sm font-semibold text-gray-800">Interfaces (from the OS)</h3>
          <span className="text-[11px] text-gray-400">
            {rateAge != null ? `rates and error counts since the previous check (${rateAge} min)` : 'rates appear after the next check'}
          </span>
        </div>
        {nics.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <th className="py-1 pr-2 font-medium">Interface</th><th className="py-1 pr-2 font-medium">State</th><th className="py-1 pr-2 font-medium">IPv4</th>
                <th className="py-1 pr-2 font-medium">Link</th><th className="py-1 pr-2 text-right font-medium">Rx</th><th className="py-1 pr-2 text-right font-medium">Tx</th>
                <th className="py-1 pr-2 text-right font-medium">Errors</th><th className="py-1 pr-2 text-right font-medium">Drops</th>
                <th className="py-1 text-right font-medium">Since boot rx / tx</th></tr></thead>
              <tbody>
                {nics.map((c) => {
                  const eni = c.mac ? eniByMac[c.mac] : null;
                  const bad = (c.errDelta || 0) > 0;
                  return (
                    <tr key={c.name} className="border-b border-gray-100 align-top">
                      <td className="py-1 pr-2">
                        <div className="font-medium text-gray-800">{c.name}</div>
                        <div className="text-[10px] text-gray-400">{eni ? `${eni.id} · ` : ''}{c.mac || ''}</div>
                      </td>
                      <td className="py-1 pr-2">
                        <span className={`rounded-full px-2 py-px text-[11px] font-medium ${c.state === 'up' ? 'bg-emerald-50 text-emerald-700' : c.state === 'down' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-500'}`}>{c.state || '—'}</span>
                      </td>
                      <td className="py-1 pr-2 text-gray-700">{c.ips && c.ips.length ? c.ips.join(', ') : '—'}</td>
                      <td className="py-1 pr-2 text-gray-500">
                        {[c.speedMbps ? (c.speedMbps >= 1000 ? `${c.speedMbps / 1000} Gb/s` : `${c.speedMbps} Mb/s`) : null, c.mtu ? `MTU ${c.mtu}` : null].filter(Boolean).join(' · ') || '—'}
                        {c.driver && <div className="text-[10px] text-gray-400">{c.driver}</div>}
                      </td>
                      <td className="py-1 pr-2 text-right tabular-nums text-gray-700">{fmtMbps(c.rxMbps)}</td>
                      <td className="py-1 pr-2 text-right tabular-nums text-gray-700">{fmtMbps(c.txMbps)}</td>
                      <td className={`py-1 pr-2 text-right tabular-nums ${bad ? 'font-semibold text-amber-700' : 'text-gray-500'}`}>
                        {c.errDelta != null ? `+${c.errDelta}` : '—'}<div className="text-[10px] font-normal text-gray-400">total {(c.rxErr || 0) + (c.txErr || 0)}</div>
                      </td>
                      <td className={`py-1 pr-2 text-right tabular-nums ${(c.dropDelta || 0) > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
                        {c.dropDelta != null ? `+${c.dropDelta}` : '—'}<div className="text-[10px] text-gray-400">total {(c.rxDrop || 0) + (c.txDrop || 0)}</div>
                      </td>
                      <td className="whitespace-nowrap py-1 text-right tabular-nums text-gray-500">{gib(c.rxBytes)} / {gib(c.txBytes)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <div className="text-xs text-gray-400">{x.osReason || 'No interface data from the OS for this instance.'}</div>}
      </div>

      {withEna.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-800">ENA limits</h3>
          <p className="mb-2 text-[11px] text-gray-500">Packets queued or dropped because the instance hit an AWS network allowance. Any increase means the instance type is too small for its traffic.</p>
          <table className="w-full text-xs">
            <tbody>
              {withEna.map((c) => Object.keys(ENA_LABEL).filter((k) => c.ena[k] != null).map((k) => {
                const d = c.enaDelta && c.enaDelta[k];
                return (
                  <tr key={c.name + k} className="border-b border-gray-100">
                    <td className="py-1 pr-2 text-gray-500">{c.name}</td>
                    <td className="py-1 pr-2 text-gray-700">{ENA_LABEL[k]}</td>
                    <td className={`py-1 pr-2 text-right tabular-nums ${d ? 'font-semibold text-red-600' : 'text-gray-500'}`}>{d ? `+${d.toLocaleString('en-US')} since last check` : 'no new events'}</td>
                    <td className="py-1 text-right tabular-nums text-gray-400">total {c.ena[k].toLocaleString('en-US')}</td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold text-gray-800">Elastic network interfaces (AWS)</h3>
        {enis.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <th className="py-1 pr-2 font-medium">Device</th><th className="py-1 pr-2 font-medium">ENI</th><th className="py-1 pr-2 font-medium">Subnet</th>
                <th className="py-1 pr-2 font-medium">Private IPs</th><th className="py-1 pr-2 font-medium">Public IP</th><th className="py-1 pr-2 font-medium">Security groups</th>
                <th className="py-1 font-medium">Source/dest check</th></tr></thead>
              <tbody>
                {enis.map((e) => (
                  <tr key={e.id} className="border-b border-gray-100 align-top">
                    <td className="py-1 pr-2 tabular-nums text-gray-500">eth{e.device ?? '?'}</td>
                    <td className="py-1 pr-2 text-gray-800">{e.id}{e.description && <div className="text-[10px] text-gray-400">{e.description}</div>}</td>
                    <td className="py-1 pr-2 text-gray-600">{e.subnet || '—'}</td>
                    <td className="py-1 pr-2 text-gray-700">{e.privateIps.map((p) => <div key={p.ip} className={p.primary ? 'font-medium' : 'text-gray-500'}>{p.ip}{p.primary ? '' : ' (secondary)'}</div>)}</td>
                    <td className="py-1 pr-2 text-gray-700">{e.privateIps.filter((p) => p.publicIp).map((p) => <div key={p.publicIp}>{p.publicIp}</div>)}{!e.privateIps.some((p) => p.publicIp) && '—'}</td>
                    <td className="py-1 pr-2 text-gray-600">{e.sgs.map((g) => <div key={g.id}>{g.name} <span className="text-[10px] text-gray-400">{g.id}</span></div>)}</td>
                    <td className="py-1">{e.srcDstCheck ? <span className="text-gray-500">on</span> : <span className="font-medium text-sky-700" title="Disabled: normal for NAT, VPN and routing instances">off (routing / NAT)</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="text-xs text-gray-400">No ENI data.</div>}
      </div>
    </div>
  );
}

// Session Manager (interactive) needs SSM agent >= 2.3.68.0; older agents still answer Run Command,
// so they show "Online" but StartSession fails with TargetNotConnected.
function ssmAgentOk(v) {
  if (!v) return true;   // unknown version: let StartSession decide
  const a = String(v).split('.').map((n) => parseInt(n, 10) || 0);
  const b = [2, 3, 68, 0];
  for (let i = 0; i < 4; i++) { if ((a[i] || 0) !== b[i]) return (a[i] || 0) > b[i]; }
  return true;
}
const ssmUsable = (x) => x.state === 'running' && x.ssmPing === 'Online' && ssmAgentOk(x.ssmAgent);

// ── Remote Console: SSH targets (same list as File Deploy → Remote Console) ──
// Loaded once per page; matched to the instance by private IP.
let sshTargetsCache = null;
function useSshTargets(enabled) {
  const [targets, setTargets] = useState(sshTargetsCache);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!enabled || sshTargetsCache) return;
    let off = false;
    api.get('/api/ssh-console/targets')
      .then((r) => {
        const self = (r.data.selfIps || []).map((ip) => ({ key: 'local', host: ip, label: `${ip} (this dashboard host)`, username: null, via: null, self: true }));
        sshTargetsCache = (r.data.targets || []).concat(self.filter((s) => !(r.data.targets || []).some((t) => t.host === s.host)));
        if (!off) setTargets(sshTargetsCache);
      })
      .catch((e) => { if (!off) setErr((e.response && e.response.data && e.response.data.error) || e.message); });
    return () => { off = true; };
  }, [enabled]);
  return { targets, err };
}

// Two ways in: SSH (key pair in SSH_TARGETS, Linux) and AWS SSM Session Manager (agent Online —
// the only option for Windows, PowerShell as SYSTEM/Administrator). Both modes stay mounted once
// opened so flipping between them keeps each session alive.
function RemoteConsoleTab({ x, account, target, targets, err }) {
  const ssmOk = ssmUsable(x);
  const modes = [];
  // Windows: Remote Desktop first (direct 3389 or SSM port forwarding — backend picks), then the SSM PowerShell.
  if (x.windows && x.state === 'running') modes.push('rdp');
  if (target) modes.push('ssh');
  if (ssmOk) modes.push('ssm');
  const [mode, setMode] = useState(modes[0] || null);
  const [opened, setOpened] = useState(() => new Set(modes[0] ? [modes[0]] : []));
  const pick = (m) => { setMode(m); setOpened((s) => (s.has(m) ? s : new Set([...s, m]))); };
  useEffect(() => { if (!mode && modes[0]) pick(modes[0]); }, [modes.join(',')]);

  if (!targets && !err && !ssmOk) return <div className="text-sm text-gray-400">Loading SSH targets…</div>;
  if (!modes.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
        No remote console available for <span className="font-mono">{x.privateIp || 'this instance'}</span>.
        <div className="mt-1 text-xs text-gray-400">
          {x.state !== 'running' ? 'Instance is not running.' : x.ssmPing === 'Online' && !ssmAgentOk(x.ssmAgent) ? (
            <>SSM agent {x.ssmAgent} is too old for Session Manager (needs 2.3.68 or newer). Update it with the Run Command document <span className="font-mono">AWS-UpdateSSMAgent</span>.</>
          ) : (
            <>SSM agent: {x.ssmPing || 'not registered'} (needs AmazonSSMManagedInstanceCore on the instance role)
              {x.windows ? '' : ' — or add the IP to SSH_TARGETS in backend/sshConsoleWs.js with its key pair'}.</>
          )}
        </div>
        {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
      </div>
    );
  }
  const sshLabel = target ? (target.via ? `${target.host} via ${target.via}` : `${target.username || 'ec2-user'}@${target.host}`) : '';
  const ssmLabel = `${x.id} (SSM${x.windows ? ' · PowerShell' : ''})`;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      {modes.length > 1 && (
        <div className="mb-3 inline-flex rounded-xl bg-slate-100/80 p-1">
          {modes.map((m) => (
            <button key={m} type="button" onClick={() => pick(m)}
              className={`rounded-lg px-3 py-1 text-xs font-medium ${mode === m ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
              {opened.has(m) ? '● ' : ''}{m === 'rdp' ? 'Remote Desktop (RDP)' : m === 'ssh' ? `SSH · ${sshLabel}` : x.windows ? 'PowerShell (SSM)' : 'SSM Session Manager'}
            </button>
          ))}
        </div>
      )}
      {opened.has('rdp') && (
        <div style={{ display: mode === 'rdp' ? 'block' : 'none' }}>
          <RdpPanel x={x} account={account} />
        </div>
      )}
      {opened.has('ssh') && target && (
        <div style={{ display: mode === 'ssh' ? 'block' : 'none' }}>
          <OsConsolePanel target={target.key} targetLabel={sshLabel} />
        </div>
      )}
      {opened.has('ssm') && ssmOk && (
        <div style={{ display: mode === 'ssm' ? 'block' : 'none' }}>
          <OsConsolePanel target={`ssm:${account}:${x.id}`} targetLabel={ssmLabel} />
        </div>
      )}
      <p className="mt-2 text-[11px] text-gray-400">
        {mode === 'rdp' ? 'Remote Desktop: connect / disconnect (who, which Windows account, path) is recorded in File Deploy → Console Log; keystrokes and screen content are not.' : (
          <>{mode === 'ssm' ? 'SSM session runs with the agent account (SYSTEM / ssm-user). ' : ''}
          Same audit as File Deploy → Console Log: command lines are recorded, output is not stored.</>)}
      </p>
    </div>
  );
}

// ── Instance modal: Overview / History / inventory tabs / Changes ────────────
function Ec2InstanceModal({ x, th, account, initialTab, onClose, onPrev, onNext, pos }) {
  const { user } = useAuth() || {};
  const isAdmin = !!(user && user.role === 'admin');
  const { targets: sshTargets, err: sshErr } = useSshTargets(isAdmin);
  const sshTarget = (sshTargets || []).find((t) => x.privateIp && t.host === x.privateIp) || null;
  // Console panel is mounted on first open and then only hidden, so switching tabs keeps the SSH session.
  const [consoleOn, setConsoleOn] = useState(false);
  useEffect(() => { setConsoleOn(false); }, [x.id]);
  const [tab, setTab] = useState(initialTab || 'overview');
  useEffect(() => { if (tab === 'console') setConsoleOn(true); }, [tab]);
  const [inv, setInv] = useState(null);
  const [invErr, setInvErr] = useState(null);
  const [chg, setChg] = useState([]);
  // Moving to another instance keeps the open tab (handy for comparing servers); tabs that
  // only some hosts have (containers, programs, cron) fall back to Overview.
  useEffect(() => { setTab((t) => (['overview', 'history', 'net', 'apps', 'svc', 'ports', 'chg', 'logs', 'top'].includes(t) ? t : 'overview')); }, [x.id]);
  useEffect(() => { setTab(initialTab || 'overview'); }, [initialTab]);
  useEffect(() => {
    let off = false;
    setInv(null); setInvErr(null); setChg([]);
    api.get(`/api/ec2-health/apps?account=${account}&instanceId=${encodeURIComponent(x.id)}&changeDays=90`)
      .then((r) => { if (off) return; setInv((r.data.instances || [])[0] || null); setChg(r.data.changes || []); })
      .catch((e) => { if (!off) setInvErr((e.response && e.response.data && e.response.data.error) || e.message); });
    return () => { off = true; };
  }, [account, x.id]);
  useEffect(() => {
    const onKey = (e) => {
      // Keys typed inside the terminal (Esc for vim, arrows for history) belong to the shell.
      if (e.target && e.target.closest && (e.target.closest('.xterm') || e.target.closest('.rdp-surface'))) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && onPrev && !/input|select|textarea/i.test(e.target.tagName)) onPrev();
      else if (e.key === 'ArrowRight' && onNext && !/input|select|textarea/i.test(e.target.tagName)) onNext();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [onClose, onPrev, onNext]);

  const v = VERDICT[x.verdict] || VERDICT.ok;
  const invOk = inv && Array.isArray(inv.apps) && (inv.apps.length || (inv.services || []).length);
  const invTabs = invOk ? inventoryTabs(inv) : [['apps', 'Applications'], ['svc', 'Services'], ['ports', 'Ports']];
  const tabs = [['overview', 'Overview'], ['history', 'History'], ['net', 'Network'], ...invTabs, ['chg', `Changes${chg.length ? ` (${chg.length})` : ''}`],
    ...(isAdmin ? [...(x.windows ? [] : [['top', 'Processes']]), ['logs', 'Logs'], ['console', `Remote Console${sshTarget || ssmUsable(x) ? '' : ' (n/a)'}`]] : [])];
  const isInvTab = !['overview', 'history', 'net', 'chg', 'console', 'logs', 'top'].includes(tab);

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-center bg-slate-900/40 p-0 backdrop-blur-[1px] sm:items-center sm:p-6" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={x.name}
        className="flex h-full w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl sm:h-[88vh] sm:rounded-2xl">
        <div className="border-b border-gray-200 px-5 pb-0 pt-4">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-bold text-gray-900">{x.name}</h2>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${v.pill}`}>{v.label}</span>
                {x.appRole && <span className="rounded bg-sky-50 px-1.5 py-px text-[11px] font-medium text-sky-700">{x.appRole}</span>}
              </div>
              <div className="mt-0.5 truncate text-xs text-gray-500">
                {x.id} · {x.type}{x.vcpu ? ` · ${x.vcpu} vCPU` : ''}{x.memGiB ? ` · ${x.memGiB} GiB` : ''} · {x.privateIp || '—'}
                {' · '}{x.osName || (x.os && x.os.osName) || x.platform}
                {x.osSource ? ` · via ${SRC_LABEL[x.osSource] || x.osSource}` : ''}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {pos && <span className="mr-1 text-[11px] tabular-nums text-gray-400">{pos}</span>}
              <button type="button" onClick={onPrev} disabled={!onPrev} title="Previous instance (←)"
                className="rounded-lg px-2 py-1 text-lg leading-none text-gray-500 hover:bg-gray-100 disabled:opacity-30">‹</button>
              <button type="button" onClick={onNext} disabled={!onNext} title="Next instance (→)"
                className="rounded-lg px-2 py-1 text-lg leading-none text-gray-500 hover:bg-gray-100 disabled:opacity-30">›</button>
              <button type="button" onClick={onClose} title="Close (Esc)"
                className="ml-1 rounded-lg px-2 py-1 text-lg leading-none text-gray-500 hover:bg-gray-100">×</button>
            </div>
          </div>
          <div className="-mb-px mt-3 flex gap-1 overflow-x-auto">
            {tabs.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setTab(k)}
                className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-sky-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/50 p-5">
          {tab === 'overview' && <InstanceOverview x={x} th={th} />}
          {tab === 'history' && <InstanceHistory account={account} instanceId={x.id} th={th} />}
          {tab === 'net' && <NetworkTab x={x} />}
          {isAdmin && tab === 'logs' && <Ec2LogsTab x={x} account={account} sshTarget={sshTarget} />}
          {isAdmin && tab === 'top' && <Ec2TopTab x={x} account={account} sshTarget={sshTarget} />}
          {isAdmin && consoleOn && (
            <div key={x.id} style={{ display: tab === 'console' ? 'block' : 'none' }}>
              <RemoteConsoleTab x={x} account={account} target={sshTarget} targets={sshTargets} err={sshErr} />
            </div>
          )}
          {isInvTab && (
            invErr ? <div className="text-sm text-red-600">{invErr}</div>
              : !inv && !invErr ? <div className="text-sm text-gray-400">Loading inventory…</div>
                : !invOk ? (
                  <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                    No application inventory for this instance{inv && inv.error ? ` — ${inv.error}` : ''}.
                  </div>
                ) : (
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="mb-3 text-[11px] text-gray-400">
                      {inv.osName || ''}{inv.pkgCount ? ` · ${inv.pkgCount} ${inv.windows ? 'programs' : 'packages'}` : ''} · scanned {ago(inv.scannedAt)}
                      {inv.error ? <span className="ml-1 text-amber-700">(last scan failed: {inv.error})</span> : null}
                    </div>
                    {inv.findings && inv.findings.length > 0 && tab === 'apps' && (
                      <ul className="mb-3 space-y-0.5 rounded-lg bg-amber-50 px-3 py-2">
                        {inv.findings.map((f) => <li key={f} className="text-xs text-amber-800">{f}</li>)}
                      </ul>
                    )}
                    <InventoryTabBody inst={inv} tab={tab} />
                  </div>
                )
          )}
          {tab === 'chg' && (
            chg.length ? (
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <table className="w-full text-xs">
                  <tbody>
                    {chg.map((c, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="whitespace-nowrap py-1 pr-3 text-gray-400">{localTime(c.ts)}</td>
                        <td className="py-1 pr-3 text-gray-500">{c.kind}</td>
                        <td className="py-1 pr-3"><span className={`rounded px-1.5 py-px text-[11px] font-medium ${c.change === 'added' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{c.change}</span></td>
                        <td className="py-1 text-gray-700">{c.item}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="text-sm text-gray-400">No application, service, port or program changes in the last 90 days.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountView({ tab, active, onSummary }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [modalTab, setModalTab] = useState('overview');
  const [osF, setOsF] = useState('all');
  const [roleF, setRoleF] = useState('all');
  const [groupBy, setGroupBy] = useState(() => { try { return localStorage.getItem(LS_GROUP) || 'none'; } catch (_) { return 'none'; } });
  const pickGroup = (k) => { setGroupBy(k); try { localStorage.setItem(LS_GROUP, k); } catch (_) {} };
  const [view, setView] = useState(() => { try { return localStorage.getItem(LS_VIEW) || 'live'; } catch (_) { return 'live'; } });
  const pickView = (k) => { setView(k); try { localStorage.setItem(LS_VIEW, k); } catch (_) {} };

  const load = useCallback(async (refresh) => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/api/ec2-health?account=${tab}${refresh ? '&refresh=1' : ''}`);
      setData(r.data);
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || e.message);
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { if (active && !data && !loading) load(false); }, [active]); // eslint-disable-line
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => load(false), AUTO_MS);
    return () => clearInterval(t);
  }, [active, load]);

  // OS / role / search narrow the scope; KPIs are computed on that scope, and the
  // verdict filter (KPI click) is applied last so the KPI numbers stay visible.
  const scoped = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return data.instances.filter((x) => (osF === 'all' || x.osType === osF)
      && (roleF === 'all' || (x.appRole || UNTAGGED) === roleF)
      && (!s || [x.name, x.id, x.privateIp, x.type, x.appRole, x.osName].some((v) => v && String(v).toLowerCase().includes(s))));
  }, [data, osF, roleF, q]);
  const list = useMemo(() => scoped.filter((x) => filter === 'all' || x.verdict === filter), [scoped, filter]);
  const osOpts = useMemo(() => countBy(data ? data.instances : [], (x) => x.osType || 'Unknown'), [data]);
  const roleOpts = useMemo(() => countBy(data ? data.instances : [], (x) => x.appRole || UNTAGGED), [data]);
  const groups = useMemo(() => {
    if (groupBy === 'none') return null;
    const keyOf = groupBy === 'os' ? (x) => x.osType || 'Unknown' : (x) => x.appRole || UNTAGGED;
    const m = new Map();
    list.forEach((x) => { const k = keyOf(x); if (!m.has(k)) m.set(k, []); m.get(k).push(x); });
    const worst = (arr) => Math.min(...arr.map((x) => RANK[x.verdict] ?? 9));
    return [...m.entries()].sort((a, b) => (a[0] === UNTAGGED) - (b[0] === UNTAGGED) || worst(a[1]) - worst(b[1]) || a[0].localeCompare(b[0]));
  }, [list, groupBy]);

  useEffect(() => {
    if (!data || !onSummary) return;
    const S = data.summary;
    onSummary(tab, `EC2 Health — account ${data.account.label} (${data.account.region}): ${S.running}/${S.total} running, `
      + `${S.critical} critical, ${S.warning} warning, ${S.ok} healthy. `
      + data.instances.filter((x) => x.reasons.length).map((x) => `${x.name}: ${x.reasons.map((r) => r.text).join('; ')}`).join(' | '));
  }, [data]); // eslint-disable-line

  if (!data && loading) return <div className="p-10 text-center text-sm text-gray-500">Collecting EC2, CloudWatch and OS metrics… (5–20 s)</div>;
  if (!data && err) return (
    <div className="m-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
      {err} <button className="ml-2 underline" onClick={() => load(true)}>Retry</button>
    </div>
  );
  if (!data) return null;

  const scopedAll = osF === 'all' && roleF === 'all' && !q.trim();
  const S = scopedAll ? data.summary : summarize(scoped);
  const th = data.thresholds;
  const fBtn = (k) => () => setFilter(filter === k ? 'all' : k);
  const openInstance = (id, t) => { setModalTab(t || 'overview'); setOpenId(id); };
  const card = (x) => <div key={x.id} id={`ec2card-${x.id}`}><InstanceCard x={x} th={th} onOpen={() => openInstance(x.id, 'overview')} /></div>;
  // prev/next walks the instances in the current scope (filters applied)
  const nav = scoped.length ? scoped : data.instances;
  const navIdx = openId ? nav.findIndex((x) => x.id === openId) : -1;
  const modalX = openId ? data.instances.find((x) => x.id === openId) : null;
  const scopeIds = scopedAll ? null : new Set(scoped.map((x) => x.id));
  const sel = 'rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700';

  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg bg-slate-100/80 p-0.5 text-sm">
        {[['live', 'Live status'], ['history', 'History & trends'], ['apps', 'Applications & services'], ['alb', 'Load balancers'], ['opensearch', 'OpenSearch']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => pickView(k)}
            className={`rounded-md px-3 py-1 font-medium ${view === k ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>
      {view === 'live' && <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Instances" value={`${S.running}/${S.total}`} sub={`running · ${S.stopped} stopped`} onClick={fBtn('all')} active={filter === 'all'} />
        <Kpi label="Critical" value={S.critical} cls={S.critical ? 'text-red-600' : 'text-gray-300'} onClick={fBtn('critical')} active={filter === 'critical'} />
        <Kpi label="Warning" value={S.warning} cls={S.warning ? 'text-amber-600' : 'text-gray-300'} onClick={fBtn('warning')} active={filter === 'warning'} />
        <Kpi label="Healthy" value={S.ok} cls="text-emerald-600" onClick={fBtn('ok')} active={filter === 'ok'} />
        <Kpi label="OS metrics" value={`${S.osCovered}/${S.running}`} sub="RAM & disk visible" />
        <Kpi label="Capacity" value={`${S.vcpu} vCPU`} sub={`${S.memGiB} GiB RAM · ${S.ebsGiB.toLocaleString('en-US')} GiB EBS`} />
      </div>}

      <div className={`${view === 'alb' || view === 'opensearch' ? 'hidden' : 'flex'} flex-wrap items-center gap-2`}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID, IP, role…"
          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm sm:w-56" />
        <select value={osF} onChange={(e) => setOsF(e.target.value)} className={sel} aria-label="OS type">
          <option value="all">All OS types</option>
          {osOpts.map(([k, n]) => <option key={k} value={k}>{k} ({n})</option>)}
        </select>
        <select value={roleF} onChange={(e) => setRoleF(e.target.value)} className={sel} aria-label="Application role">
          <option value="all">All application roles</option>
          {roleOpts.map(([k, n]) => <option key={k} value={k}>{k} ({n})</option>)}
        </select>
        {view === 'live' && <div className="inline-flex items-center rounded-lg bg-slate-100/80 p-0.5 text-xs">
          <span className="px-2 text-gray-500">Group by</span>
          {[['none', 'None'], ['os', 'OS type'], ['role', 'App role']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => pickGroup(k)}
              className={`rounded-md px-2.5 py-1 font-medium ${groupBy === k ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
          ))}
        </div>}
        {!scopedAll && (
          <button type="button" onClick={() => { setOsF('all'); setRoleF('all'); setQ(''); }} className="text-xs text-sky-600 hover:underline">Clear filters</button>
        )}
        {view === 'live' && <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
          <span>Updated {fmtAgo(data.generatedAt)}{data.cached ? ' (cached)' : ''}</span>
          <button type="button" onClick={() => load(true)} disabled={loading}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>}
      </div>

      {view === 'history' && <AccountTrends account={tab} scopeIds={scopeIds} onOpenInstance={(id) => openInstance(id, 'history')} />}
      {view === 'apps' && <Ec2AppsView account={tab} scopeIds={scopeIds} onOpenInstance={openInstance} />}
      {view === 'alb' && <Ec2AlbView account={tab} />}
      {view === 'opensearch' && <Ec2OpenSearchView account={tab} />}
      {modalX && (
        <Ec2InstanceModal x={modalX} th={th} account={tab} initialTab={modalTab} onClose={() => setOpenId(null)}
          pos={navIdx >= 0 ? `${navIdx + 1} / ${nav.length}` : null}
          onPrev={navIdx > 0 ? () => setOpenId(nav[navIdx - 1].id) : null}
          onNext={navIdx >= 0 && navIdx < nav.length - 1 ? () => setOpenId(nav[navIdx + 1].id) : null} />
      )}

      {data.errors && data.errors.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">Partial data: {data.errors.join(' · ')}</div>
      )}

      {view !== 'live' ? null : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">No instances match.</div>
      ) : groups ? (
        <div className="space-y-5">
          {groups.map(([k, arr]) => {
            const c = arr.filter((x) => x.verdict === 'critical').length;
            const w = arr.filter((x) => x.verdict === 'warning').length;
            return (
              <section key={k}>
                <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-1.5">
                  <h3 className={`text-sm font-semibold ${k === UNTAGGED ? 'italic text-gray-500' : 'text-gray-800'}`}>{k}</h3>
                  <span className="text-xs text-gray-400">{arr.length} instance{arr.length > 1 ? 's' : ''}</span>
                  {c > 0 && <span className="rounded-full bg-red-100 px-2 py-px text-[11px] font-semibold text-red-700">{c} critical</span>}
                  {w > 0 && <span className="rounded-full bg-amber-100 px-2 py-px text-[11px] font-semibold text-amber-700">{w} warning</span>}
                  {!c && !w && <span className="rounded-full bg-emerald-100 px-2 py-px text-[11px] font-semibold text-emerald-700">all healthy</span>}
                </div>
                <div className="grid items-start gap-3 md:grid-cols-2 2xl:grid-cols-3">{arr.map(card)}</div>
              </section>
            );
          })}
        </div>
      ) : (
        <div className="grid items-start gap-3 md:grid-cols-2 2xl:grid-cols-3">{list.map(card)}</div>
      )}

      {view === 'live' && <div className="text-[11px] leading-relaxed text-gray-400">
        Thresholds: CPU {th.cpuWarn}/{th.cpuCrit}% (15-min average) · RAM {th.memWarn}/{th.memCrit}% · Disk {th.diskWarn}/{th.diskCrit}% (warning/critical).
        CPU, network and status checks come from CloudWatch; RAM and disk from the CloudWatch Agent, SSM Run Command or SSH (read-only).
        Sybase data/log devices are pre-allocated and always near full, so they are shown but not counted. Auto-refresh every 5 minutes; a snapshot is stored every 15 minutes for history.
      </div>}
    </div>
  );
}

export default function AwsEc2Health() {
  const valid = (k) => TABS.some((t) => t.key === k);
  const [tab, setTab] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('tab');
      if (p && valid(p)) return p;
      const s = localStorage.getItem(LS_TAB);
      return s && valid(s) ? s : 'indomas';
    } catch (_) { return 'indomas'; }
  });
  useTabDeepLink('aws_ec2_health', valid, setTab);
  const pick = (k) => { setTab(k); try { localStorage.setItem(LS_TAB, k); } catch (_) {} };
  const [summaries, setSummaries] = useState({});
  const onSummary = useCallback((k, s) => setSummaries((m) => (m[k] === s ? m : Object.assign({}, m, { [k]: s }))), []);
  usePageAIContext('aws_ec2_health', 'EC2 Health', summaries[tab] || null);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">EC2 Health</h1>
        <p className="text-sm text-gray-500">One verdict per instance — CPU, RAM, disk and status checks across both AWS accounts.</p>
      </div>
      <div className="inline-flex rounded-2xl bg-slate-100/80 p-1">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => pick(t.key)}
            className={`rounded-xl px-4 py-1.5 text-left transition ${tab === t.key ? 'bg-white shadow-sm ring-1 ring-gray-200' : 'hover:bg-white/60'}`}>
            <div className={`text-sm font-semibold ${tab === t.key ? 'text-gray-900' : 'text-gray-500'}`}>{t.label}</div>
            <div className="text-[10px] text-gray-400">{t.sub}</div>
          </button>
        ))}
      </div>
      {TABS.map((t) => (
        <div key={t.key} style={{ display: tab === t.key ? 'block' : 'none' }}>
          <AccountView tab={t.key} active={tab === t.key} onSummary={onSummary} />
        </div>
      ))}
    </div>
  );
}
